import { isElectronApp } from '#~/electron/utils.js';
// ENG-3716: surface restore failures to the waiting-for-video-track state
// machine instead of swallowing them in a fire-and-forget promise.
import { failVideoTrackWait } from '#~/livekit/track-wait.js';
import { logger } from '#~/logger/index.js';
import {
  extractMachineIDAndTypeFromPersistentID,
  getLocalSessionID,
  locateDeviceByPersistentID,
  safeMachinePersistentID
} from './discovery.js';
import {
  MediaDevicePipeline,
  tearDownPipelineIfNecessary,
  localDeviceIdURXMapping,
  parseURX,
  pipelines
} from './pipeline.js';

export const autoRestorePipelineForDeviceIfNecessaryFromURX = async (
  urx: string,
  agentID: string,
  deviceId: string
): Promise<void> => {
  if (!isElectronApp('worker')) {
    return;
  }

  if (urx.includes('browsermedia://')) {
    if (localDeviceIdURXMapping.has(deviceId)) {
      if (urx === localDeviceIdURXMapping.get(deviceId)) {
        return;
      }

      localDeviceIdURXMapping.delete(deviceId);
      await tearDownPipelineIfNecessary(deviceId);
    }

    // ENG-3716: this call used to be fire-and-forget, so a restore failure
    // ('No media device ID found' when the snapshot device id is absent from
    // live discovery) became an unhandled rejection and the source sat on
    // "Connecting..." forever. Await it — restore failures are settled (never
    // rethrown) inside, keyed into the waiting-for-video-track state machine.
    await autoRestorePipelineForDeviceIfNecessary(agentID, deviceId, urx);
  }
};

export const autoRestorePipelineForDeviceIfNecessary = async (
  agentID: string,
  deviceId: string,
  urx: string
): Promise<void> => {
  if (pipelines.has(deviceId)) {
    return;
  }

  const { persistentID } = parseURX(urx);

  if (!persistentID) {
    return;
  }

  let kind: 'audio' | 'video' | undefined;

  // ENG-3716: validate the snapshot-restored device id against live discovery
  // BEFORE committing to a pipeline start. A persisted id absent from the live
  // device list can never publish — fail it into a concrete UI state
  // ("previous camera not discovered") instead of letting the start throw be
  // swallowed and the source hang on "Connecting...".
  let liveDevice: Awaited<ReturnType<typeof locateDeviceByPersistentID>>;

  try {
    // Eligibility decodes untrusted snapshot IDs too. Keep it inside the same
    // boundary so corrupt entries cannot abort restoration of healthy siblings.
    const eligibility = await checkIfDeviceIsElegableForAutoRestore(persistentID, deviceId);
    kind = eligibility[1];
    if (!eligibility[0] || kind === undefined) return;
    liveDevice = await locateDeviceByPersistentID(kind, persistentID);
  } catch (error) {
    // Per rule settle-every-terminal-branch (ai.knowledge/global): a corrupt or
    // undecodable persisted id makes the lookup itself throw (decode/parse
    // errors are rethrown by discovery). Settle it exactly like an absent
    // device — the persisted id can never be located — and never rethrow: the
    // boot snapshot sync awaits this restore in a loop, so a propagated error
    // would abort the restore of every subsequent device.
    logger.warning(
      `Auto-restore failed for device ${deviceId}: persisted device id could not be resolved: ${error}`
    );

    if (kind !== 'audio') {
      failVideoTrackWait(deviceId, 'device-not-discovered');
    }

    return;
  }

  if (!liveDevice) {
    logger.warning(
      `Auto-restore skipped for device ${deviceId}: persisted media device is absent from live discovery.`
    );

    if (kind === 'video') {
      failVideoTrackWait(deviceId, 'device-not-discovered');
    }

    return;
  }

  const pipeline = new MediaDevicePipeline(agentID, deviceId, kind, persistentID, urx);

  // Per rule settle-every-terminal-branch (ai.knowledge/global): a start
  // failure must settle into the state machine (and logs), never escape as an
  // unhandled rejection — the boot snapshot sync that reaches here must keep
  // processing other devices.
  try {
    await pipeline.start();
  } catch (error) {
    logger.warning(`Failed to restore browser media pipeline for device ${deviceId}: ${error}`);

    if (kind === 'video') {
      // The device disappeared between validation and start ('No media device
      // ID found') → not discovered; anything else (room connect/publish
      // failure) reads as the camera being unreachable.
      failVideoTrackWait(
        deviceId,
        String(error).includes('No media device ID found')
          ? 'device-not-discovered'
          : 'camera-unreachable'
      );
    }

    return;
  }

  markDeviceElegableForAutoRestore(kind, persistentID, deviceId, true);
};

export const markDeviceElegableForAutoRestore = async (
  _kind: 'audio' | 'video',
  _persitentID: string,
  _deviceId: string,
  _force: boolean = false
): Promise<void> => {
  //getLocalSessionID();
  //const browserSourcedDevicesRaw = await localStorage.get('browserSourcedDevices');
  //const browserSourcedDevices = browserSourcedDevicesRaw ? JSON.parse(browserSourcedDevicesRaw) : {};
  /*
  if(deviceId?.startsWith('tmp:')) {
    tempBrowserSourcedDevices[deviceId] = { persitentID, kind };
    return;
  }
*/
  //browserSourcedDevices[deviceId] = { persitentID, kind };
  //await localStorage.set('browserSourcedDevices', JSON.stringify(browserSourcedDevices));
};

const checkIfDeviceIsElegableForAutoRestore = async (
  persitentID: string,
  _deviceId: string
): Promise<[boolean, 'audio' | 'video' | undefined]> => {
  const { machinePersistentID, machineSessionID, kind } =
    extractMachineIDAndTypeFromPersistentID(persitentID);
  // A parseable JSON value is not necessarily a usable persisted device ID.
  if (
    (kind !== 'audio' && kind !== 'video') ||
    typeof machineSessionID !== 'string' ||
    typeof machinePersistentID !== 'string'
  ) {
    throw new Error('Invalid persisted media device ID');
  }
  const currentMachinePersistentID = await safeMachinePersistentID();

  if (getLocalSessionID() === machineSessionID) {
    return [true, kind];
  }

  if (machinePersistentID === currentMachinePersistentID) {
    return [true, kind];
  }

  return [false, undefined];
};
